import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { RootState, AppDispatch, store } from '../../store/store';
import type { ProductView } from '../../api/generated/models/ProductView';
import type { PaymentCreateRequest } from '../../api/generated/models/PaymentCreateRequest';
import type { SubscriptionView } from '../../api/generated/models/SubscriptionView';
import { createPayment } from '../../store/slices/invoiceSlice';
import { CouponService } from '../../api/generated/services/CouponService';
import { fetchProducts, fetchUserLicenses, setPaymentPending, clearPaymentPending } from '../../store/slices/licenseSlice';
import { addNotification } from '../../store/slices/notificationSlice';
import LicenseBuyCard from '../../components/license-buy/LicenseBuyCard';
import LicenseAddonCard from '../../components/license-buy/LicenseAddonCard';
import LicenseChangePlanModal, { ChangePlanSuccessPayload } from './LicenseChangePlanModal';
import LicensePendingPaymentModal, { PendingInvoice } from './LicensePendingPaymentModal';
import Modal from '../ui/Modal';
import Input from '../ui/Input';
import { helper } from '../../utils/helper';
import { isDataSourceConfigured } from '../../utils/dataSourceHelpers';
import { getStoredCouponCode } from '../../utils/referralCookies';
import {
  AppliedCoupon,
  appliedCouponFromView,
  applyCouponDiscount,
  couponDurationLabel,
  couponReasonI18nKey,
} from '../../utils/couponReasons';
import {
  canChangePlan,
  findPendingScheduledChange,
  isTrialSubscription,
} from '../../utils/subscriptionHelpers';

interface LicenseBuyCardsProps {
  licenseType: string;
  className?: string;
}

const LicenseBuyCards: React.FC<LicenseBuyCardsProps> = ({
  licenseType,
  className = ''
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { contact } = useSelector((state: RootState) => state.profile);
  const { currentPayment } = useSelector((state: RootState) => state.invoices);
  const { items: userSubscriptions, isPaymentPending } = useSelector((state: RootState) => state.licenses);

  const isSubuser = !!contact?.parentUserId;

  const [billingPeriod, setBillingPeriod] = useState<'yearly' | 'monthly'>('yearly');
  const [paymentWindow, setPaymentWindow] = useState<Window | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalState, setModalState] = useState<'loading' | 'confirm' | 'success' | 'payment_pending'>('loading');
  const [selectedProduct, setSelectedProduct] = useState<ProductView | null>(null);
  const [totalPriceCents, setTotalPriceCents] = useState<number>(0);
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
  const [monthlyProducts, setMonthlyProducts] = useState<ProductView[]>([]);
  const [yearlyProducts, setYearlyProducts] = useState<ProductView[]>([]);
  // Add-on products (`pluginName` = `<licenseType>_addon_*`) come back from the
  // same `GET /products?plugin_name=<licenseType>` call - rendered in a
  // separate "Add-ons" section below the plan cards, split by the same toggle.
  const [monthlyAddonProducts, setMonthlyAddonProducts] = useState<ProductView[]>([]);
  const [yearlyAddonProducts, setYearlyAddonProducts] = useState<ProductView[]>([]);
  const [subscriptionsCountBeforePurchase, setSubscriptionsCountBeforePurchase] = useState<number>(0);
  const products = useSelector((state: RootState) => state.licenses.products);

  // Coupon: prefilled from the `s2g_coupon` cookie, sent with the payment
  // request. Stripe computes the final price; GET /coupon/validate below only
  // drives the UI hint (prices are recalculated locally for display).
  const [couponCode, setCouponCode] = useState<string>(() => getStoredCouponCode() || '');
  const [couponError, setCouponError] = useState<string | null>(null);
  // Set after a successful validate - reset on every edit of the field.
  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(null);

  /**
   * Validates the coupon for the current category (coupon scope = product
   * `pluginName`, so any product of the category works as `productId`).
   * `valid: false` shows the localized reason at the field; transport
   * failures stay silent - POST /payment re-validates the code anyway.
   */
  const checkCoupon = useCallback(async (code: string, productId: string | undefined) => {
    const trimmed = code.trim();
    if (!trimmed || !productId) {
      setAppliedCoupon(null);
      setCouponError(null);
      return;
    }
    try {
      const response = await CouponService.getAppCouponValidate(trimmed, productId);
      if (response.valid) {
        setAppliedCoupon(appliedCouponFromView(response));
        setCouponError(null);
      } else {
        setAppliedCoupon(null);
        setCouponError(t(couponReasonI18nKey(response.reason)));
      }
    } catch {
      setAppliedCoupon(null);
    }
  }, [t]);

  // Any non-addon product of the current category serves as `productId` for
  // validate (all products of a category are equivalent for a coupon).
  const couponValidateProductId = useMemo(
    () => products.find((p) => p.pluginName === licenseType)?.id,
    [products, licenseType]
  );

  // Validate the (cookie-prefilled or typed) code once the category's
  // products are known, and again whenever the category changes - the
  // coupon scope depends on it.
  useEffect(() => {
    if (couponCode.trim()) {
      checkCoupon(couponCode, couponValidateProductId);
    }
    // Only category/product changes re-trigger; typing is validated on blur.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponValidateProductId, checkCoupon]);

  /** "25 %" or "10,00 €" */
  const formatCouponAmount = (coupon: AppliedCoupon): string =>
    coupon.discountType === 'percent'
      ? `${coupon.discountValue} %`
      : helper.formatPrice({ amount: coupon.discountValue, currency: coupon.currency || 'EUR' });


  // Change-plan modal state. Direction is computed locally by comparing prices,
  // but the server is the source of truth and confirms it in the response.
  const [changePlanTarget, setChangePlanTarget] = useState<ProductView | null>(null);
  // Freshdesk add-on counts selected for the pending change-plan, if any.
  // `undefined` = don't send (keep current). Set from the originating card.
  const [changePlanEmailExtra, setChangePlanEmailExtra] = useState<number | undefined>(undefined);
  const [changePlanAgentExtra, setChangePlanAgentExtra] = useState<number | undefined>(undefined);
  // True while a change-plan request (and any follow-up 3DS popup / polling) is
  // in flight. Blocks the purchase / switch / change-options buttons on every
  // card, mirroring `isPaymentPending` during a regular purchase.
  const [changePlanInProgress, setChangePlanInProgress] = useState(false);

  // 409 `pending_payment` modal state - a previous failed 3DS left an open
  // Stripe invoice that must be paid before any new plan change is allowed.
  const [pendingInvoice, setPendingInvoice] = useState<PendingInvoice | null>(null);

  // Track in-flight upgrade/sidegrade polls to avoid stacking timers across
  // multiple actions during a single page lifetime.
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => () => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
  }, []);

  // `?scroll=addons` deep-link (menu / add-on-required modal): scroll to the
  // Add-ons section once it is rendered, once per navigation.
  const addonsSectionRef = useRef<HTMLDivElement | null>(null);
  const scrollHandledForRef = useRef<string | null>(null);
  const hasAddonProducts =
    monthlyAddonProducts.length > 0 || yearlyAddonProducts.length > 0;
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('scroll') !== 'addons') return;
    if (!addonsSectionRef.current) return;
    if (scrollHandledForRef.current === location.key) return;
    scrollHandledForRef.current = location.key;
    addonsSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location, hasAddonProducts]);

  useEffect(() => {
    dispatch(fetchProducts(licenseType));
  }, [dispatch, licenseType]);

  // "Owns this product" predicate for the cards. A TRIAL subscription is
  // deliberately NOT counted as ownership even though its productId points at
  // a regular paid product: the trial card must keep the normal buy CTA
  // (checkout) and must NOT show the Cancel button - change-plan/cancel are
  // Stripe-only and there is nothing to cancel on a trial.
  const isOwnedSubscription = (subscription: SubscriptionView, productId: string): boolean =>
    subscription.productId === productId &&
    subscription.isActive &&
    !isTrialSubscription(subscription) &&
    (!subscription.expired || new Date(subscription.expired) > new Date());

  // Check if user has an active subscription for a product
  const hasActiveSubscription = (productId: string): boolean => {
    if (!userSubscriptions || userSubscriptions.length === 0) {
      return false;
    }
    
    return userSubscriptions.some(subscription => isOwnedSubscription(subscription, productId));
  };

  // Get active subscription ID for a product
  const getActiveSubscriptionId = (productId: string): string | undefined => {
    if (!userSubscriptions || userSubscriptions.length === 0) {
      return undefined;
    }
    
    const activeSubscription = userSubscriptions.find(subscription => isOwnedSubscription(subscription, productId));
    
    return activeSubscription?.id;
  };

  // Get active subscription for a product (full object)
  const getActiveSubscription = (productId: string) => {
    if (!userSubscriptions || userSubscriptions.length === 0) {
      return undefined;
    }
    
    return userSubscriptions.find(subscription => isOwnedSubscription(subscription, productId));
  };

  /**
   * Active subscription within THIS plugin (any product), if any. Drives the
   * change-plan UI: when present and eligible, every other paid product card
   * shows "Switch to this plan" instead of "Select".
   */
  const pluginActiveSubscription: SubscriptionView | undefined = useMemo(() => {
    if (!userSubscriptions || userSubscriptions.length === 0) return undefined;
    return userSubscriptions.find((s) =>
      s.pluginName === licenseType &&
      s.isActive &&
      (!s.expired || new Date(s.expired) > new Date())
    );
  }, [userSubscriptions, licenseType]);

  /** True when the active subscription is eligible for change-plan (Stripe & not cancelled). */
  const pluginCanChangePlan = !!(
    pluginActiveSubscription && canChangePlan(pluginActiveSubscription)
  );

  /**
   * Active TRIAL subscription for this plugin (auto-granted on registration).
   * The card of its product shows a trial note above the buy CTA.
   */
  const pluginTrialSubscription: SubscriptionView | undefined =
    pluginActiveSubscription && isTrialSubscription(pluginActiveSubscription)
      ? pluginActiveSubscription
      : undefined;

  /** Pending scheduled downgrade row (Subscription B), inferred via heuristic. */
  const pendingScheduledChange: SubscriptionView | undefined = useMemo(() => {
    if (!pluginActiveSubscription) return undefined;
    return findPendingScheduledChange(userSubscriptions, pluginActiveSubscription);
  }, [pluginActiveSubscription, userSubscriptions]);

  /** Resolved product name of B (for badge & modal copy). */
  const pendingScheduledProductName: string | undefined = useMemo(() => {
    if (!pendingScheduledChange) return undefined;
    const product = products.find((p) => p.id === pendingScheduledChange.productId);
    return product?.name ?? pendingScheduledChange.productName;
  }, [pendingScheduledChange, products]);
  
  useEffect(() => {
    if (products.length === 0) return;
    let monthly: ProductView[] = [];
    let yearly: ProductView[] = [];
    let monthlyAddons: ProductView[] = [];
    let yearlyAddons: ProductView[] = [];

    products.forEach(product => {
      // Determine if yearly based on duration in seconds (12 months = 12 * 30 * 24 * 60 * 60)
      const durationMonths = Math.round(product.durationSec / (30 * 24 * 60 * 60));
      const isYearly = durationMonths >= 12;

      // Add-on products are returned in the same response but must not appear
      // in the main plan grid - they get their own section below it.
      if (product.pluginName.startsWith(`${licenseType}_addon_`)) {
        if (isYearly) {
          yearlyAddons.push(product);
        } else {
          monthlyAddons.push(product);
        }
        return;
      }

      // Genuinely-free plans are shown on BOTH tabs. Enterprise/contact-only
      // plans also have priceCents === 0 but must NOT be doubled - they are
      // split into monthly/yearly by their actual duration like paid plans.
      const isFree = product.priceCents === 0 && !product.isContactOnly;

      if (isYearly || isFree) {
        yearly.push(product);
      }
      if (!isYearly || isFree) {
        monthly.push(product);
      }
    });

    setMonthlyProducts(monthly);
    setYearlyProducts(yearly);
    setMonthlyAddonProducts(monthlyAddons);
    setYearlyAddonProducts(yearlyAddons);
  }, [products, licenseType]);


  // Payment popup logic
  const openPaymentPopup = (url: string) => {
    const width = 1000;
    const height = 800;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      url,
      'PaymentWindow',
      `width=${width},height=${height},left=${left},top=${top},toolbar=0,location=0,status=0,menubar=0,scrollbars=1,resizable=1`
    );

    if (popup) {
      setPaymentWindow(popup);
      const checkWindow = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkWindow);
          setPaymentWindow(null);
          dispatch(fetchProducts(licenseType));
          setTimeout(() => {
            dispatch(fetchUserLicenses({ page: 1, pageSize: 100 }));
          }, 100);
        }
      }, 1000);
    } else {
      dispatch(addNotification({
        type: 'warning',
        title: t('attention'),
        message: t('license_popup_blocked'),
      }));
    }
  };

  // Cleanup payment window on unmount
  useEffect(() => {
    return () => {
      if (paymentWindow && !paymentWindow.closed) {
        paymentWindow.close();
      }
    };
  }, [paymentWindow]);

  /**
   * Open the change-plan modal for a target product, carrying optional
   * Freshdesk add-on counts. Direction/amounts are derived by the server in
   * preview + change-plan; we only forward the user's intent here.
   */
  const handleChangePlan = (
    target: ProductView,
    emailExtraCount?: number,
    agentExtraCount?: number,
  ) => {
    if (!pluginCanChangePlan || !pluginActiveSubscription) return;
    setChangePlanEmailExtra(emailExtraCount);
    setChangePlanAgentExtra(agentExtraCount);
    setChangePlanTarget(target);
  };

  /**
   * Called by `LicenseChangePlanModal` when the backend rejects a change-plan
   * with 409 `pending_payment`. We just stash the invoice - the parent decides
   * the UX (mounts the dedicated PendingPayment modal in the JSX below).
   */
  const handlePendingPayment = (invoice: PendingInvoice) => {
    // The change-plan was NOT applied - the dedicated pending-payment modal
    // takes over, so release the change-plan button lock.
    setChangePlanInProgress(false);
    setPendingInvoice(invoice);
  };

  /**
   * After the user closes the Stripe popup for the pending invoice, poll
   * `/subscriptions` for up to 30s. If the active subscription's productId
   * flipped, the failed-3DS upgrade went through after this retry - toast
   * success and close the pending-payment modal. Otherwise leave the modal
   * open so the user can click "Complete payment" again or contact support.
   */
  const startPendingPaymentPolling = (subscriptionId: string, oldProductId: string) => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    const start = Date.now();
    const interval = setInterval(async () => {
      try {
        const result = await dispatch(
          fetchUserLicenses({ page: 1, pageSize: 100, silent: true })
        ).unwrap();
        const updated = result.items.find((s) => s.id === subscriptionId);
        if (updated && updated.productId !== oldProductId) {
          clearInterval(interval);
          pollIntervalRef.current = null;
          dispatch(fetchProducts(licenseType));
          dispatch(addNotification({
            type: 'success',
            title: t('notification_success'),
            message: t('subscription_pending_payment_paid_toast'),
            autoHideDuration: 6000,
          }));
          setPendingInvoice(null);
          return;
        }
        if (Date.now() - start > 30000) {
          clearInterval(interval);
          pollIntervalRef.current = null;
        }
      } catch (err) {
        console.error('[Pending-Payment Polling] error:', err);
      }
    }, 2000);
    pollIntervalRef.current = interval;
  };

  /**
   * Open the Stripe hosted invoice page for a pending invoice. Reuses the same
   * centred-popup UX as the regular Stripe Checkout flow. Captures the active
   * sub's productId at click time so the polling on close can detect a real
   * state change (vs the user closing without paying).
   */
  const openPendingInvoicePopup = (url: string) => {
    const width = 1000;
    const height = 800;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      url,
      'PendingInvoiceWindow',
      `width=${width},height=${height},left=${left},top=${top},toolbar=0,location=0,status=0,menubar=0,scrollbars=1,resizable=1`,
    );

    if (!popup) {
      dispatch(addNotification({
        type: 'warning',
        title: t('attention'),
        message: t('license_popup_blocked'),
      }));
      return;
    }

    setPaymentWindow(popup);
    const subId = pluginActiveSubscription?.id;
    const oldProductId = pluginActiveSubscription?.productId;
    const checkWindow = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkWindow);
        setPaymentWindow(null);
        if (subId && oldProductId) {
          startPendingPaymentPolling(subId, oldProductId);
        } else {
          dispatch(fetchUserLicenses({ page: 1, pageSize: 100, silent: true }));
        }
      }
    }, 1000);
  };

  /**
   * Poll `/subscriptions` for up to 30s after an upgrade/sidegrade until the
   * active subscription's productId actually flips. Stripe webhook normally
   * arrives within seconds, but we don't want to block the UI on it - we just
   * want to refresh the cards once the change is visible.
   */
  const startUpgradePolling = (subscriptionId: string, oldProductId: string) => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    const start = Date.now();
    const interval = setInterval(async () => {
      try {
        const result = await dispatch(
          fetchUserLicenses({ page: 1, pageSize: 100, silent: true })
        ).unwrap();
        const updated = result.items.find((s) => s.id === subscriptionId);
        if (updated && updated.productId !== oldProductId) {
          clearInterval(interval);
          pollIntervalRef.current = null;
          setChangePlanInProgress(false);
          dispatch(fetchProducts(licenseType));
          return;
        }
        if (Date.now() - start > 30000) {
          clearInterval(interval);
          pollIntervalRef.current = null;
          setChangePlanInProgress(false);
        }
      } catch (err) {
        console.error('[Change-Plan Polling] error:', err);
      }
    }, 2000);
    pollIntervalRef.current = interval;
  };

  /**
   * Called by the change-plan modal AFTER a successful 202 from the backend.
   * The modal closes itself in all cases - this handler decides what UX to
   * surface based on the server-confirmed direction and Stripe invoice status.
   *
   * Trust `serverDirection` (NOT the preview's direction) - the spec
   * explicitly says preview can disagree if a billing-cycle webhook lands
   * between the two calls.
   */
  const handleChangePlanSuccess = (payload: ChangePlanSuccessPayload) => {
    const {
      serverDirection,
      effectiveAt,
      alreadyScheduled,
      status,
      hostedInvoiceUrl,
    } = payload;

    if (serverDirection === 'downgrade') {
      const dateLabel = effectiveAt
        ? new Date(effectiveAt * 1000).toLocaleDateString()
        : '-';
      dispatch(addNotification({
        type: alreadyScheduled ? 'info' : 'success',
        title: t('notification_success'),
        message: alreadyScheduled
          ? t('subscription_change_toast_already_scheduled')
          : t('subscription_change_toast_downgrade_scheduled', { date: dateLabel }),
        autoHideDuration: 6000,
      }));
      dispatch(fetchUserLicenses({ page: 1, pageSize: 100, silent: true }));
      // Downgrade is scheduled instantly - nothing else in flight.
      setChangePlanInProgress(false);
      return;
    }

    // Sidegrade is now synchronous (`applied: true`, no invoice / no wait).
    // Just refresh once and toast - the productId may not even change (e.g.
    // Freshdesk same-product option swap), so polling for a productId flip
    // would be pointless.
    if (serverDirection === 'sidegrade') {
      dispatch(addNotification({
        type: 'success',
        title: t('notification_success'),
        message: t('subscription_change_toast_sidegrade_success'),
        autoHideDuration: 6000,
      }));
      dispatch(fetchUserLicenses({ page: 1, pageSize: 100, silent: true }));
      dispatch(fetchProducts(licenseType));
      setChangePlanInProgress(false);
      return;
    }

    // Upgrade.
    // 3DS / SCA path: anything that isn't "paid" AND has a hosted invoice URL
    // means Stripe needs the user to complete the payment manually. The
    // hosted-invoice page is the same UX surface as our existing Stripe
    // Checkout popup, so reuse `openPaymentPopup`.
    const needsPayment = status !== 'paid' && !!hostedInvoiceUrl;
    if (needsPayment && hostedInvoiceUrl) {
      openPaymentPopup(hostedInvoiceUrl);
      // Start polling immediately - webhook will land once the user pays and
      // we want the cards to flip without an extra refresh. Polling clears the
      // in-progress flag when it settles.
      if (pluginActiveSubscription) {
        startUpgradePolling(pluginActiveSubscription.id, pluginActiveSubscription.productId);
      } else {
        setChangePlanInProgress(false);
      }
      return;
    }

    // status === 'paid' (or the rare paid-without-url case): treat as success -
    // refresh and poll for the local productId to flip once the webhook lands.
    dispatch(addNotification({
      type: 'success',
      title: t('notification_success'),
      message: t('subscription_change_toast_upgrade_success'),
      autoHideDuration: 6000,
    }));

    if (pluginActiveSubscription) {
      startUpgradePolling(pluginActiveSubscription.id, pluginActiveSubscription.productId);
    } else {
      setChangePlanInProgress(false);
    }
  };

  // Handle subscription purchase
  const handlePurchase = async (product: ProductView, emailExtraCount?: number, agentExtraCount?: number, calculatedTotalPrice?: number) => {
    if (!contact || product.priceCents === undefined) {
      console.error(t('license_missing_info'));
      return;
    }

    // Contact-only (Enterprise) plans cannot be bought online - the backend
    // rejects payment for them. Guard here as well in case a buy action leaks.
    if (product.isContactOnly) {
      return;
    }

    // Save current subscriptions count BEFORE purchase
    setSubscriptionsCountBeforePurchase(userSubscriptions.length);

    setSelectedProduct(product);
    setTotalPriceCents(calculatedTotalPrice || product.priceCents);
    setIsModalOpen(true);
    setModalState('loading');

    setCouponError(null);

    const request: PaymentCreateRequest = {
      productId: product.id,
      emailExtraCount: emailExtraCount || null,
      agentExtraCount: agentExtraCount || null,
      couponCode: couponCode.trim() || null,
    };

    try {
      const response = await dispatch(createPayment(request)).unwrap();
      setModalState('confirm');
      if (response.paymentLink) {
        setPaymentUrl(response.paymentLink);
      }
    } catch (error: any) {
      setIsModalOpen(false);
      // Coupon rejections (400) arrive via `rejectWithValue` - show them at
      // the coupon field; the user can clear the code and pay without it.
      const couponApiError = error?.couponError as string | undefined;
      if (couponApiError) {
        setAppliedCoupon(null);
        setCouponError(t(couponReasonI18nKey(couponApiError)));
      }
    }
  };

  // Modal handlers
  const handleConfirm = () => {
    setModalState('success');
    if (paymentUrl) {
      openPaymentPopup(paymentUrl);
      setModalState('payment_pending');
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  // Handle payment pending modal close
  const handlePaymentPendingClose = () => {
    setIsModalOpen(false);
    dispatch(clearPaymentPending());
  };

  // Handle payment confirmed
  const handlePaymentConfirmed = () => {
    setIsModalOpen(false);
    if (selectedProduct?.id) {
      dispatch(setPaymentPending({ 
        productId: selectedProduct.id, 
        licensesCount: subscriptionsCountBeforePurchase 
      }));
      startSubscriptionCheck(subscriptionsCountBeforePurchase);
    }
  };

  // Check for subscription changes after payment
  const startSubscriptionCheck = (initialCount: number) => {
    const checkInterval = setInterval(async () => {
      try {
        const result = await dispatch(fetchUserLicenses({ page: 1, pageSize: 100, silent: true })).unwrap();
        
        if (result.items.length > initialCount) {
          clearInterval(checkInterval);
          dispatch(clearPaymentPending());
          
          dispatch(addNotification({
            type: 'success',
            title: t('success'),
            message: t('license_payment_success'),
          }));

          const boughtContentMaster = selectedProduct?.pluginName === 'content_master';
          const dsNow = store.getState().dataSource.data;
          if (boughtContentMaster) {
            if (!isDataSourceConfigured(dsNow)) {
              navigate('/profile?direction=dataSource', { replace: true });
            } else {
              navigate('/features/content-master', { replace: true });
            }
          } else {
            navigate('/dashboard', { replace: true });
          }
          window.scrollTo(0, 0);
        }
      } catch (error) {
        console.error('[Subscription Check] Error fetching subscriptions:', error);
      }
    }, 5000);

    // Stop checking after 5 minutes
    setTimeout(() => {
      clearInterval(checkInterval);
      dispatch(clearPaymentPending());
    }, 300000);
  };

  return (
    <>
      <div className={`license-buy-cards ${className}`}>
        <div className="license-buy-cards-container">
          <h2 className="block-title">
            {licenseType === 'mobile'
              ? t('cards_section.title_mobile')
              : t('cards_section.title')}
          </h2>
          
          {/* Billing Period Toggle */}
          <div className="billing-period-toggle">
            <button
              className={`toggle-button ${billingPeriod === 'monthly' ? 'active' : ''}`}
              onClick={() => setBillingPeriod('monthly')}
            >
              {t('cards_section.card.monthly')}
            </button>
            <button
              className={`toggle-button ${billingPeriod === 'yearly' ? 'active' : ''}`}
              onClick={() => setBillingPeriod('yearly')}
            >
              {t('cards_section.card.yearly')}
            </button>
          </div>

          {/* Affiliate coupon - the code is sent with the payment request;
              the backend applies the discount (netto, before VAT), so Stripe
              Checkout opens with the already discounted price. */}
          <div className="affiliate-coupon-field">
            <Input
              id="couponCode"
              label={t('affiliate.coupon_label')}
              value={couponCode}
              error={couponError || undefined}
              onChange={(value) => {
                setCouponCode(value);
                setAppliedCoupon(null);
                if (couponError) setCouponError(null);
              }}
              onBlur={() => checkCoupon(couponCode, couponValidateProductId)}
            />
            {!couponError && appliedCoupon && (
              <div className="affiliate-coupon-applied">
                {t('affiliate.coupon_applied_label', {
                  amount: formatCouponAmount(appliedCoupon),
                  code: appliedCoupon.code,
                })}
                {' · '}
                {couponDurationLabel(t, appliedCoupon.duration, appliedCoupon.durationInMonths)}
              </div>
            )}
          </div>

           <div className="license-cards-grid">
             {(() => {
               const visibleProducts = billingPeriod === 'yearly' ? yearlyProducts : monthlyProducts;
               // For content_master: compute the minimum `token_included_count`
               // across the currently displayed products so the "Nx More Credits"
               // badge on each card scales relative to the cheapest plan.
               let minTokenIncludedCount: number | undefined;
               if (licenseType === 'content_master') {
                 const counts: number[] = [];
                 visibleProducts.forEach(p => {
                   const raw = p.options?.token_included_count?.value;
                   if (raw === undefined || raw === null || String(raw).trim() === '') return;
                   const parsed = parseInt(String(raw).trim(), 10);
                   if (Number.isFinite(parsed) && parsed > 0) counts.push(parsed);
                 });
                 if (counts.length > 0) minTokenIncludedCount = Math.min(...counts);
               }
               return visibleProducts.map(product => {
                 const activeSubscription = getActiveSubscription(product.id);
                 const isThisActiveCard = !!activeSubscription;
                 // Eligible target for change-plan: another paid product in the
                 // same plugin, where the active sub is Stripe + not cancelled.
                 const cardCanChangePlan = pluginCanChangePlan
                   && !isThisActiveCard
                   && product.priceCents > 0
                   && product.isActive
                   && !product.isContactOnly
                   && product.id !== pluginActiveSubscription?.productId;
                 // Freshdesk allows changing add-on options (extra emails /
                 // agents) on the SAME product. Expose that on the active
                 // Freshdesk card when the subscription is change-eligible.
                 const allowOptionsChange = licenseType === 'freshdesk'
                   && isThisActiveCard
                   && pluginCanChangePlan
                   && !isSubuser;
                 return (
                   <LicenseBuyCard
                     key={product.id}
                     product={product}
                     licenseType={licenseType}
                     billingPeriod={billingPeriod}
                     couponDiscount={appliedCoupon}
                     onPurchase={handlePurchase}
                     hasActiveLicense={hasActiveSubscription(product.id)}
                     activeLicenseId={getActiveSubscriptionId(product.id)}
                     isPaymentPending={isPaymentPending}
                     isChangePlanInProgress={changePlanInProgress}
                     isCancelled={activeSubscription?.isCancelled}
                     expirationDate={activeSubscription?.expired}
                     minTokenIncludedCount={minTokenIncludedCount}
                     pluginCanChangePlan={cardCanChangePlan}
                     allowOptionsChange={allowOptionsChange}
                     pendingScheduledProductName={
                       isThisActiveCard ? pendingScheduledProductName : undefined
                     }
                     isSubuser={isSubuser}
                     onChangePlan={handleChangePlan}
                     trialExpirationDate={
                       pluginTrialSubscription?.productId === product.id
                         ? pluginTrialSubscription?.expired
                         : undefined
                     }
                   />
                 );
               });
             })()}
           </div>

          {(() => {
            const visibleAddons = billingPeriod === 'yearly' ? yearlyAddonProducts : monthlyAddonProducts;
            if (visibleAddons.length === 0) return null;
            return (
              <div className="license-addons-section" ref={addonsSectionRef}>
                <h3 className="license-addons-title">{t('cards_section.addons_title')}</h3>
                {visibleAddons.map(addon => {
                  // Match the active add-on subscription by the addon's
                  // pluginName (not productId): the section shows one card per
                  // billing period, but the owned subscription may be the other
                  // period's product - the card must still reflect it (and not
                  // offer a second, parallel purchase of the same add-on).
                  const activeAddonSubscription = (userSubscriptions || []).find(s =>
                    s.pluginName === addon.pluginName &&
                    s.isActive &&
                    (!s.expired || new Date(s.expired) > new Date())
                  );
                  return (
                    <LicenseAddonCard
                      key={addon.id}
                      product={addon}
                      licenseType={licenseType}
                      onPurchase={handlePurchase}
                      hasActiveLicense={!!activeAddonSubscription}
                      activeLicenseId={activeAddonSubscription?.id}
                      isCancelled={activeAddonSubscription?.isCancelled}
                      expirationDate={activeAddonSubscription?.expired}
                      isPaymentPending={isPaymentPending}
                      isSubuser={isSubuser}
                      canPurchase={!!pluginActiveSubscription}
                    />
                  );
                })}
              </div>
            );
          })()}

          <div className="vat-warning">
            <p><b>{t('cards_section.vat_warning_title')}</b> {t('cards_section.vat_warning_text')}</p>
          </div>
        </div>
      </div>

      {/* Purchase Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        isCentered={true}
        hideCloseButton={true}
        title={
          modalState === 'loading' ? t('license_preparing') :
            modalState === 'confirm' ? selectedProduct?.name || t('license_purchase_confirmation') :
              modalState === 'success' ? selectedProduct?.name || t('license_purchase_confirmation') :
                t('license_payment_processing')
        }
      >
        {modalState === 'loading' && (
          <div className="modal-loading">
            <div className="modal-loading-spinner" />
            <div className="modal-loading-text">{t('license_preparing_text')}</div>
            <div className="modal-loading-subtext">{t('license_please_wait')}</div>
          </div>
        )}

        {modalState === 'confirm' && selectedProduct && (
          <div className="modal-success">
            <div className="modal-success-text">{t('license_confirm_payment')}</div>
            <div className="modal-details">
              <div className="modal-details-item">
                <span className="modal-details-label">{t('license_price')} </span>
                <span className="modal-details-value">
                  {appliedCoupon ? (
                    <>
                      <s className="affiliate-coupon-old-price">
                        {helper.formatPrice({ amount: totalPriceCents, currency: 'EUR' })}
                      </s>{' '}
                      {helper.formatPrice({ amount: applyCouponDiscount(totalPriceCents, appliedCoupon), currency: 'EUR' })}
                    </>
                  ) : (
                    helper.formatPrice({ amount: totalPriceCents, currency: 'EUR' })
                  )}
                </span>
              </div>
            </div>
            <div className="modal-buttons">
              <button
                className="modal-button cancel"
                onClick={handleCancel}
              >
                {t('license_cancel')}
              </button>
              <button
                className="modal-button confirm"
                onClick={handleConfirm}
              >
                {t('license_confirm')}
              </button>
            </div>
          </div>
        )}

        {modalState === 'success' && selectedProduct && (
          <div className="modal-success">
            <div className="modal-success-title">{t('license_created')}</div>
            <div className="modal-success-text">{t('license_proceed_payment')}</div>
            <div className="modal-details">
              <div className="modal-details-item">
                <span className="modal-details-label">{t('license_product')}</span>
                <span className="modal-details-value">{selectedProduct.name}</span>
              </div>
              <div className="modal-details-item">
                <span className="modal-details-label">{t('license_price')} </span>
                <span className="modal-details-value">
                  {appliedCoupon ? (
                    <>
                      <s className="affiliate-coupon-old-price">
                        {helper.formatPrice({ amount: totalPriceCents, currency: 'EUR' })}
                      </s>{' '}
                      {helper.formatPrice({ amount: applyCouponDiscount(totalPriceCents, appliedCoupon), currency: 'EUR' })}
                    </>
                  ) : (
                    helper.formatPrice({ amount: totalPriceCents, currency: 'EUR' })
                  )}
                </span>
              </div>
            </div>
          </div>
        )}

        {modalState === 'payment_pending' && (
          <div className="modal-payment-pending">
            <div className="modal-payment-pending-text">
              {t('license_payment_pending_text')}
            </div>
            <div className="modal-payment-pending-text">
              {t('license_payment_pending_close')}
            </div>
            <div className="modal-payment-pending-text">
              {t('license_payment_pending_notify')}
            </div>
            <div className="modal-buttons">
              <button className="modal-button cancel" onClick={handlePaymentPendingClose}>
                {t('license_close')}
              </button>
              <button className="modal-button confirm" onClick={handlePaymentConfirmed}>
                {t('license_payment_confirmed')}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Change Plan Modal - direction, amounts and effective date are sourced
          from the `/change-plan/preview` call inside the modal itself. The
          current plan name comes from the subscription, so we don't depend on
          the active product being present in the loaded catalog. */}
      {changePlanTarget && pluginActiveSubscription && (
        <LicenseChangePlanModal
          isOpen={!!changePlanTarget}
          onClose={() => {
            setChangePlanTarget(null);
            setChangePlanEmailExtra(undefined);
            setChangePlanAgentExtra(undefined);
          }}
          subscriptionId={pluginActiveSubscription.id}
          currentProductName={pluginActiveSubscription.productName}
          newProduct={changePlanTarget}
          emailExtraCount={changePlanEmailExtra}
          agentExtraCount={changePlanAgentExtra}
          hasPendingScheduledChange={!!pendingScheduledChange}
          pendingPlanName={pendingScheduledProductName}
          onSuccess={handleChangePlanSuccess}
          onSubmittingChange={setChangePlanInProgress}
          onPendingPayment={handlePendingPayment}
        />
      )}

      {/* Pending Payment Modal - surfaced when change-plan returns 409
          `pending_payment`. The CTA reuses the Stripe-popup UX. */}
      {pendingInvoice && (
        <LicensePendingPaymentModal
          isOpen={!!pendingInvoice}
          onClose={() => setPendingInvoice(null)}
          pendingInvoice={pendingInvoice}
          onPay={openPendingInvoicePopup}
        />
      )}
    </>
  );
};

export default LicenseBuyCards;
